import { useEffect, useRef, useState } from 'react';
import { getApiClient } from '../api';
import type { Municipalities } from '../api';
import { resolveBrowseGroup } from '../utils/municipalityGroups';
import type { BrowseGroup } from '../utils/municipalityGroups';

export interface GroupStats {
    municipalityCount: number;
    sessionCount: number;
    voteCount: number;
    memberCount: number;
}

export interface GlobalStats extends GroupStats {
    byGroup: Record<BrowseGroup, GroupStats>;
}

const emptyGroupStats = (): GroupStats => ({ municipalityCount: 0, sessionCount: 0, voteCount: 0, memberCount: 0 });

const emptyStats = (): GlobalStats => ({
    ...emptyGroupStats(),
    byGroup: { kraj: emptyGroupStats(), mesto: emptyGroupStats(), national: emptyGroupStats() }
});

/**
 * Aggregates headline numbers (sessions/votes/members) across every tracked
 * municipality's body, for its latest term — both overall and broken down by
 * browse group. There is no backend aggregate endpoint yet, so this fans out
 * per municipality × body — acceptable while the tracked list stays small,
 * but a candidate for a dedicated endpoint if it grows.
 */
export function useGlobalStats(municipalities: Municipalities[] | undefined) {
    const [stats, setStats] = useState<GlobalStats | null>(null);
    const [loading, setLoading] = useState(true);
    const abortRef = useRef<AbortController | null>(null);

    useEffect(() => {
        if (!municipalities || municipalities.length === 0) {
            setStats(emptyStats());
            setLoading(false);
            return;
        }

        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        const options = { signal: controller.signal };
        setLoading(true);

        const api = getApiClient();
        const bodyPairs = municipalities.flatMap((municipality) =>
            Object.keys(municipality.organy ?? {}).map((bodyCode) => ({
                textId: municipality.text_id,
                bodyCode,
                group: resolveBrowseGroup(municipality.typ_code)
            }))
        );

        const fetchBodyTotals = async ({ textId, bodyCode, group }: { textId: string; bodyCode: string; group: BrowseGroup }) => {
            const councils = await api.getCouncilsByMunicipalityIdAndBody(textId, bodyCode, options);
            if (!councils.length) {
                return null;
            }
            // Already ordered by poradi desc on the backend — first entry is the latest term.
            const generalData = await api.getGeneralDataByMunicipalityIdAndBodyAndCouncilId(textId, bodyCode, councils[0].poradi, options);
            return { group, generalData };
        };

        Promise.allSettled(bodyPairs.map(fetchBodyTotals)).then((results) => {
            if (controller.signal.aborted) {
                return;
            }

            const next = emptyStats();
            next.municipalityCount = municipalities.length;
            for (const municipality of municipalities) {
                next.byGroup[resolveBrowseGroup(municipality.typ_code)].municipalityCount += 1;
            }

            for (const result of results) {
                if (result.status !== 'fulfilled' || !result.value) {
                    continue;
                }
                const { group, generalData } = result.value;
                const memberCount = generalData.pocet_zastupitelu + generalData.pocet_zastupitelek;

                next.sessionCount += generalData.pocet_zasedani;
                next.voteCount += generalData.pocet_hlasovani;
                next.memberCount += memberCount;

                next.byGroup[group].sessionCount += generalData.pocet_zasedani;
                next.byGroup[group].voteCount += generalData.pocet_hlasovani;
                next.byGroup[group].memberCount += memberCount;
            }

            setStats(next);
            setLoading(false);
        });

        return () => controller.abort();
    }, [municipalities]);

    return { stats, loading };
}
